/**
 * Check-off actions (docs/DESIGN.md §3b; core corrections.ts): the one Undo toast, "Not done after all" and the struck
 * row. Every screen that checks something off uses these; none draws its own.
 *  - useCheckoff().done(what, saved): "Done ✓ ‹what›" with Undo for 30 s. Undo voids it on the server (kept, marked undone) (or drops the
 *    queued job so it never sends) and calls `onUndone` so the row goes back to not done.
 *  - <NotDoneButton>: a quiet Action on a done row, only for whoever may correct it (lib/checkoff.ts revertVisible).
 *    It opens <RevertSheet>: "Why?" (three Answer chips), an optional note, Save.
 *  - <Struck>: the original, struck through, with the correction beside it as Info.
 */
import { useState, type ReactNode } from "react";
import { REVERT_NOTE_MAX, REVERT_REASONS, correctionLine, landedRecord, recordDestination, type Correction, type DestinationKey, type RevertReason, type Role } from "@logwave/core";
import { useLanded } from "../lib/landed.ts";
import type { RevertBody } from "../api.ts";
import { doneText, revertVisible, undoSaved, type Saved } from "../lib/checkoff.ts";
import { saveErrorText } from "../lib/failures.ts";
import { useOutbox } from "../lib/outbox.tsx";
import { useApi, useSessionMaybe } from "../session.tsx";
import { Icon, type IconName } from "./Icons.tsx";
import { AnswerGroup, Button, ErrorBox, Field, Sheet, useToast } from "./ui.tsx";

export interface DoneOpts {
  /** The row goes back to not done (reload, or flip local state). */
  onUndone?: () => void;
  /** Words instead of "Done ✓ ‹what›" (quick log keeps "Took out the kitchen trash. Logged 2:14 PM ✓"). */
  text?: string;
  /** Where the toast's link goes instead of today's records (the first record's "See your binder"). */
  link?: { label: string; to: string };
  icon?: IconName;
  /** A second line ("Saved: Sanitizer log") and the cleaver's chop in place of the icon (a clean Easy-steps log). */
  sub?: string;
  chop?: boolean;
  /**
   * Show the Landed card instead of the toast (peek and resume, core landed.ts): "Restroom check → cleaning log / Your
   * manager sees it", tap to peek at today's records. `where`: the screen the person stays on ("Checks"). A save still
   * waiting on this device (offline) has no record yet, so it keeps the plain toast.
   */
  landed?: { where: string; templateId?: string; category?: string; destination?: DestinationKey; /** Line 1's short name ("Cold brew"), when `what` is long. */ what?: string };
}

export function useCheckoff() {
  const api = useApi();
  const toast = useToast();
  const outbox = useOutbox(api);
  const landed = useLanded();
  const me = useSessionMaybe()?.me.user.name;
  return {
    /** Show the check-off toast. `saved` null (nothing to take back): the toast without Undo. */
    done(what: string, saved: Saved | null, opts: DoneOpts = {}) {
      const text = opts.text ?? doneText(what);
      // Every save links to the record (audit "Is it working?"): "Saved · See it in today's records". A save still
      // waiting on this device (offline) has no record yet, so no link.
      const link = saved && "jobId" in saved ? undefined : (opts.link ?? RECORDS_LINK);
      const extra = { ...(opts.sub ? { sub: opts.sub } : {}), ...(opts.chop ? { chop: true } : {}) };
      const undoIt = saved ? async () => {
        const r = await undoSaved(saved, { api, drop: outbox.drop });
        if (r.ok) { opts.onUndone?.(); toast.show(`Undone: ${what}. It stays in the history, marked undone.`, { icon: "undo" }); }
        else toast.show(r.message, { icon: "alert" });
      } : undefined;
      if (opts.landed && !(saved && "jobId" in saved)) {
        const l = opts.landed;
        landed(landedRecord({ what: l.what ?? what, destination: l.destination ?? recordDestination({ templateId: l.templateId, category: l.category, what }), ...(saved && "ref" in saved ? { recordId: saved.ref.id } : {}), undo: !!saved, ...(me ? { by: { name: me } } : {}), now: Date.now() }),
          { ...(undoIt ? { undo: undoIt } : {}), where: l.where, back: l.where });
        return;
      }
      if (!saved) { toast.show(text, { icon: opts.icon ?? "check", ...(link ? { link } : {}), ...extra }); return; }
      toast.show(text, {
        ...extra,
        ...(link ? { link } : {}),
        icon: opts.icon ?? ("jobId" in saved ? "clock" : "check"),
        undo: undoIt!,
      });
    },
  };
}

/** The save's link to its record (components/TodayRecords.tsx). */
export const RECORDS_LINK = { label: "See it in today's records", to: "/#records" };

const REASON_LABELS = REVERT_REASONS.map((r) => r.label);

/** "Not done after all": why (the three chips), an optional note, Save. `save` sends it (revertCheckoff / revertChore). */
export function RevertSheet({ what, save, onClose, onDone }: { what: string; save: (b: RevertBody) => Promise<unknown>; onClose: () => void; onDone?: () => void }) {
  const toast = useToast();
  const [label, setLabel] = useState<string>();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string>();
  const reason = REVERT_REASONS.find((r) => r.label === label)?.key as RevertReason | undefined;
  async function submit() {
    if (!reason) { setErr("Pick why it wasn't done."); return; }
    setBusy(true); setErr(undefined);
    try {
      await save({ reason, ...(note.trim() ? { note: note.trim() } : {}) });
      toast.show(`Marked not done: ${what}. It's due again; the record keeps both.`, { icon: "undo" });
      onDone?.();
      onClose();
    } catch (e) { setErr(saveErrorText(e)); } finally { setBusy(false); }
  }
  return (
    <Sheet title="Not done after all" kicker={what} onClose={onClose}>
      <p className="muted">It stays on the record, struck through, with who marked it and why. The item is due again.</p>
      <AnswerGroup label="Why?" name={`Why wasn't it done: ${what}`} options={REASON_LABELS} value={label} onChange={setLabel} />
      <Field label="Anything to add? (optional)">{(id) => <input id={id} type="text" maxLength={REVERT_NOTE_MAX} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Only wiped half the bar" />}</Field>
      {err && <ErrorBox error={err} />}
      <div className="btn-row">
        <Button busy={busy} icon="check" onClick={() => void submit()}>Mark not done</Button>
        <Button variant="quiet" onClick={onClose}>Cancel</Button>
      </div>
    </Sheet>
  );
}

/**
 * The quiet "Not done after all" on a done row, shown only to whoever may correct it. `item`: who did it and when (and
 * any correction already on it). `me`: the viewer. `tz`: the place's time zone (the doer's "same day").
 */
export function NotDoneButton({ what, item, me, tz, save, onDone, label = "Not done after all" }: {
  what: string; item: { by: string; at: string; correction?: Correction | null }; me: { userId: string; role: Role }; tz?: string;
  save: (b: RevertBody) => Promise<unknown>; onDone?: () => void; label?: string;
}) {
  const [open, setOpen] = useState(false);
  if (!revertVisible(item, me, tz)) return null;
  return (
    <>
      <Button small variant="quiet" icon="undo" aria-haspopup="dialog" aria-label={`${label}: ${what}`} onClick={() => setOpen(true)}>{label}</Button>
      {open && <RevertSheet what={what} save={save} onClose={() => setOpen(false)} onDone={onDone} />}
    </>
  );
}

/** The original of a corrected row, struck through (still readable), with the correction beside it as Info. */
export function Struck({ children, correction, name, timeLabel }: { children: ReactNode; correction?: Correction | null; name?: string; timeLabel?: (iso: string) => string }) {
  if (!correction) return <>{children}</>;
  return (
    <span className="struck">
      <s>{children}</s>
      <span className="struck-note"><Icon name="undo" size={14} /> {correctionLine(correction, { name, timeLabel })}</span>
    </span>
  );
}
